import { Button, ButtonStyles, Icons, SelectList } from '@silverfort/sf-components';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { downloadFile } from '../../../components/utils';
import { DateFormat, formatDate } from '../../../utils/dates';
import { useGetShadowAdminsReportState } from '../api/queries/useGetShadowAdminsReportState';
import { useGenerateShadowAdminsReport } from '../api/queries/useGenerateShadowAdminsReport';


interface ExportShadowAdminsDataProps {
    exportDisplayedData: () => void;
}

type ActionType = 'generate' | 'download' | 'export';

interface ActionItem {
    value: ActionType;
    title: string;
    disabled?: boolean;
}

interface ShadowAdminsActions {
    generate: () => Promise<void>;
    download: () => void;
    export: () => void;
}

const ExportShadowAdminsData: React.FC<ExportShadowAdminsDataProps> = ({ exportDisplayedData }) => {
    const [isReportAvailable, setIsReportAvailable] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string>('');
    const [fileDate, setFileDate] = useState<string>('');
    const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false);
    const { reportState, error: reportStateError } = useGetShadowAdminsReportState();
    const { generateReport: generateReportMutation, loading: isGenerating, error: generateError } = useGenerateShadowAdminsReport();

    useEffect(() => {
        if (reportStateError) {
            console.error('Failed to fetch report state:', reportStateError);
            return;
        }
        if (reportState) {
            const { shadowAdminsReportFileName, shadowAdminsReportDate, isShadowAdminsReportAvailable } = reportState;
            
            const date = new Date(shadowAdminsReportDate);
            const formattedDate = formatDate(date, DateFormat.ISO_DATETIME, true);
            const formattedFileDate = formatDate(date, DateFormat.FILE, true);

            setFileName(`${shadowAdminsReportFileName}${formattedFileDate}.csv`);
            setFileDate(formattedDate);
            setIsReportAvailable(isShadowAdminsReportAvailable);
        }
    }, [reportState, reportStateError]);

    const generateReport = useCallback(async (): Promise<void> => {
        const downloadData = await generateReportMutation();

        if (generateError) {
            console.error('Failed to generate report:', generateError);
            return;
        }

        if (downloadData) {
            const { shadowAdminsReportGenerated, shadowAdminsReportFileName, shadowAdminsReportDate } = downloadData;

            if (shadowAdminsReportGenerated) {
                const date = new Date(shadowAdminsReportDate);
                const formattedDate = formatDate(date, DateFormat.ISO_DATETIME, true);
                const formattedFileDate = formatDate(date, DateFormat.FILE, true);

                setIsReportAvailable(true);
                setFileName(`${shadowAdminsReportFileName}${formattedFileDate}.csv`);
                setFileDate(formattedDate);
            }
        }
    }, [generateReportMutation, generateError]);

    const downloadReport = useCallback((): void => {
        if (!isReportAvailable || !fileName) {
            console.warn('No report available for download');
            return;
        }

        setIsDownloadingReport(true);
        try {
            downloadFile('/insights/downloadLastShadowAdminsReport', fileName);
        } catch (error) {
            console.error('Failed to download report:', error);
        } finally {
            setIsDownloadingReport(false);
        }
    }, [isReportAvailable, fileName]);

    const actions: ShadowAdminsActions = useMemo(
        () => ({
            generate: generateReport,
            download: downloadReport,
            export: exportDisplayedData
        }),
        [generateReport, downloadReport, exportDisplayedData]
    );

    const handleActionChange = useCallback(
        (selectedItem: ActionType | ActionType[] | undefined): void => {
            if (!selectedItem || Array.isArray(selectedItem)) {
                return;
            }

            const action = actions[selectedItem];

            if (action) {
                action();
            } else {
                console.warn(`Unknown action: ${selectedItem}`);
            }
        },
        [actions]
    );

    const getActionItems = useCallback((): ActionItem[] => {
        const items: ActionItem[] = [
            {
                value: 'generate',
                title: isGenerating ? 'Generating Report...' : 'Generate Shadow Admins Report (with reasons)',
                disabled: isGenerating
            },
            {
                value: 'download',
                title: isDownloadingReport
                    ? 'Downloading Report...'
                    : isReportAvailable
                    ? `Download Shadow Admins Report (${fileDate})`
                    : 'Download Shadow Admins Report (N/A)',
                disabled: !isReportAvailable || isDownloadingReport
            },
            {
                value: 'export',
                title: 'Export Shadow Admins list (without reasons)'
            }
        ];

        return items.filter((item) => !item.disabled);
    }, [isGenerating, isDownloadingReport, isReportAvailable, fileDate]);

    return (
        <SelectList
            TriggerComponent={Button}
            items={getActionItems()}
            menuClassName="w-[350px]"
            menuProps={{
                disableAutoFocus: true
            }}
            onChange={handleActionChange}
            triggerProps={{
                // @ts-ignore
                text: <div className="font-normal text-sf-gray-800 text-13 font-sf">Shadow Admin Report</div>,
                className: 'w-[350px] h-full bg-sf-gray-50 border-none',
                iconRight: Icons.ChevronDown,
                buttonStyle: ButtonStyles.Ghost
            }}
        />
    );
};

export default ExportShadowAdminsData;
